// The BI `componentName` for a link tapped inside a chat message.
//
// Every in-chat link renders through ONE anchor renderer (`Markdown`'s `a`), so the two
// CTAs the BI QA review named — "connect your calendar" and "premium plans" — are told
// apart by their DESTINATION rather than by finding each place the assistant might emit
// them. Anything else reports the generic `chat_link`, and the raw url always rides
// `additionalFields`, so a destination nobody has classified yet is still attributed.
//
// Shared 1:1 with the native apps (`ChatLinkBI.swift` / `ChatLinkBi.kt`) — same markers,
// same three outputs, same case-insensitivity — so the three surfaces report the same
// component for the same link. `test/chat-link-bi.test.ts` pins the vocabulary.

/** Destination substrings that mark a link as the calendar-connect CTA. `connectors/…google-calendar`
 *  is what the deeplink resolver turns `settings/calendars` into; the bare `calendars` path is the
 *  deeplink the assistant actually writes. */
const CALENDAR_MARKERS = ['settings/calendars', 'google-calendar', 'calendar-connect'];

/** Destination substrings that mark a link as the premium/plans CTA. Both real destinations are
 *  here: the Wixel pricing page and the Symphony plan picker (which of the two ships is
 *  experiment-gated), plus the `packages` path they both land on. */
const PREMIUM_MARKERS = ['wixel-pricing', 'packages', 'premium', 'checkout'];

export const CHAT_LINK_COMPONENT = {
  calendar: 'chat_link_calendar',
  premium: 'chat_link_premium',
  other: 'chat_link',
} as const;

/**
 * Classify an in-chat link by where it goes. `href` may be any markdown target — an
 * `orion://` deeplink, an in-app route, an external url — or undefined, which is still
 * classified (as the generic link) rather than throwing.
 */
export function chatLinkBiComponent(href: string | undefined): string {
  const haystack = (href ?? '').toLowerCase();
  if (CALENDAR_MARKERS.some((m) => haystack.includes(m))) return CHAT_LINK_COMPONENT.calendar;
  if (PREMIUM_MARKERS.some((m) => haystack.includes(m))) return CHAT_LINK_COMPONENT.premium;
  return CHAT_LINK_COMPONENT.other;
}
